import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../lib/project-context';
import { Settings2, BarChart3, FileBarChart, Bot, X, ArrowRight, ChevronRight, Sparkles, Layers } from 'lucide-react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

/* ═══════════════════════════════════════════
   POST-ONBOARDING BANNER
   Shows after first onboarding completion
   ═══════════════════════════════════════════ */
export function PostOnboardingBanner({ projectName }) {
  const navigate = useNavigate();
  const { activeProject, activeScenario } = useProject();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('post-onboarding-dismissed') === 'true');

  if (dismissed) return null;

  const scenarioId = activeScenario?.id;
  const projectId = activeProject?.id;

  const actions = [
    {
      icon: Settings2,
      title: 'Refinar tu modelo',
      desc: 'Ajusta las variables para acercarte a la realidad',
      color: 'var(--accent-primary)',
      bg: 'var(--accent-subtle)',
      onClick: () => projectId && scenarioId ? navigate(`/analyzer/wizard/${projectId}/${scenarioId}`) : navigate('/analyzer'),
    },
    {
      icon: BarChart3,
      title: 'Revisar sensibilidad',
      desc: 'Descubre qué variable impacta más la TIR',
      color: 'var(--market-primary)',
      bg: 'var(--market-light)',
      onClick: () => navigate('/analyzer'),
    },
    {
      icon: FileBarChart,
      title: 'Generar un reporte',
      desc: 'Comparte resultados con tu equipo o inversionistas',
      color: 'var(--construction-primary)',
      bg: 'var(--construction-light)',
      onClick: () => navigate('/reports'),
    },
    {
      icon: Bot,
      title: 'Preguntar al Copilot',
      desc: 'Pregunta sobre riesgos, viabilidad o cualquier duda',
      color: 'var(--accent-primary)',
      bg: 'var(--accent-subtle)',
      onClick: () => navigate('/copilot'),
    },
  ];

  const handleDismiss = () => {
    localStorage.setItem('post-onboarding-dismissed', 'true');
    setDismissed(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className="rounded-xl p-5 mb-6 relative"
      style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)' }}
      data-testid="post-onboarding-banner"
    >
      <button onClick={handleDismiss} className="absolute top-3 right-3 p-1 rounded-lg transition-colors hover:bg-[var(--bg-hover)]" data-testid="dismiss-banner">
        <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
        <h3 className="text-sm font-display font-semibold" style={{ color: 'var(--text-primary)' }}>
          {projectName ? `${projectName} listo` : 'Tu proyecto está listo'}
        </h3>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>¿Qué te gustaría hacer ahora?</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        {actions.map((a, i) => (
          <button
            key={i}
            onClick={a.onClick}
            className="flex items-start gap-3 p-3 rounded-lg text-left transition-all duration-150 group"
            style={{ border: '1px solid var(--border-subtle)', background: 'var(--bg-primary)' }}
            onMouseOver={(e) => e.currentTarget.style.borderColor = a.color}
            onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
            data-testid={`guided-action-${i}`}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: a.bg }}>
              <a.icon className="w-4 h-4" style={{ color: a.color }} />
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{a.title}</div>
              <div className="text-[10px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>{a.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════
   GUIDED TOUR — Tooltip-based
   ═══════════════════════════════════════════ */
const TOUR_STEPS = [
  {
    target: '[data-testid="project-switcher"]',
    title: 'Proyecto activo',
    content: 'Aquí siempre puedes ver y cambiar el proyecto en el que estás trabajando.',
    position: 'bottom',
  },
  {
    target: '[data-testid="scenario-switcher"]',
    title: 'Escenario activo',
    content: 'Cada proyecto puede tener múltiples escenarios. Selecciona uno para analizar.',
    position: 'bottom',
  },
  {
    target: '[data-testid="sidebar-nav-analyzer"]',
    title: 'Financial Intelligence',
    content: 'Aquí ejecutas el análisis financiero completo: KPIs, gráficos, flujo mensual y sensibilidad.',
    position: 'right',
  },
  {
    target: '[data-testid="sidebar-nav-copilot"]',
    title: 'Copilot Financiero',
    content: 'Tu analista virtual. Pregunta sobre riesgos, viabilidad o cualquier aspecto del proyecto.',
    position: 'right',
  },
  {
    target: '[data-testid="sidebar-nav-reports"]',
    title: 'Reportes',
    content: 'Genera reportes profesionales para compartir con inversionistas o tu equipo.',
    position: 'right',
  },
];

export function GuidedTour() {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tourCompleted = localStorage.getItem('guided-tour-completed') === 'true';

  useEffect(() => {
    const justOnboarded = localStorage.getItem('onboarding_completed') === 'true';
    if (justOnboarded && !tourCompleted) {
      const timer = setTimeout(() => setActive(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [tourCompleted]);

  const updatePosition = useCallback(() => {
    if (!active) return;
    const step = TOUR_STEPS[currentStep];
    if (!step) return;
    const el = document.querySelector(step.target);
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pos = step.position || 'bottom';
    let top, left;
    if (pos === 'bottom') {
      top = rect.bottom + 10;
      left = rect.left + rect.width / 2;
    } else if (pos === 'right') {
      top = rect.top + rect.height / 2;
      left = rect.right + 14;
    }
    setPosition({ top, left, pos });
  }, [active, currentStep]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [updatePosition]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(s => s + 1);
    } else {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setActive(false);
    localStorage.setItem('guided-tour-completed', 'true');
  };

  if (!active) return null;

  const step = TOUR_STEPS[currentStep];
  const isLast = currentStep === TOUR_STEPS.length - 1;
  const isRight = position.pos === 'right';

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-[998]" style={{ background: 'rgba(0,0,0,0.15)' }} onClick={handleDismiss} />
      {/* Tooltip */}
      <motion.div
        key={currentStep}
        initial={{ opacity: 0, y: isRight ? 0 : 8, x: isRight ? 8 : 0 }}
        animate={{ opacity: 1, y: 0, x: 0 }}
        className="fixed z-[999] w-[260px] rounded-xl shadow-lg p-4"
        style={{
          top: position.top,
          left: isRight ? position.left : position.left - 130,
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-default)',
        }}
        data-testid="guided-tour-tooltip"
      >
        {/* Arrow */}
        <div
          className="absolute w-2.5 h-2.5 rotate-45"
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-default)',
            borderRight: isRight ? 'none' : undefined,
            borderBottom: isRight ? 'none' : undefined,
            borderLeft: !isRight ? 'none' : undefined,
            borderTop: !isRight ? 'none' : undefined,
            top: isRight ? '50%' : '-6px',
            left: isRight ? '-6px' : '50%',
            transform: isRight ? 'translateY(-50%) rotate(45deg)' : 'translateX(-50%) rotate(45deg)',
          }}
        />
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-display font-semibold" style={{ color: 'var(--text-primary)' }}>{step.title}</h4>
          <span className="text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>{currentStep + 1}/{TOUR_STEPS.length}</span>
        </div>
        <p className="text-[11px] leading-relaxed mb-3" style={{ color: 'var(--text-secondary)' }}>{step.content}</p>
        <div className="flex items-center gap-2">
          <button onClick={handleDismiss} className="text-[11px] font-medium" style={{ color: 'var(--text-muted)' }}>Saltar</button>
          <div className="flex-1" />
          <Button
            onClick={handleNext}
            className="h-7 px-3 text-[11px] text-white font-medium"
            style={{ background: 'var(--accent-primary)' }}
            data-testid="tour-next-btn"
          >
            {isLast ? 'Entendido' : 'Siguiente'} {!isLast && <ChevronRight className="ml-0.5 w-3 h-3" />}
          </Button>
        </div>
      </motion.div>
    </>
  );
}


/* ═══════════════════════════════════════════
   CONTEXT INDICATOR — Active project/scenario
   ═══════════════════════════════════════════ */
export function ActiveContextBar() {
  const { activeProject, activeScenario } = useProject();

  if (!activeProject) return null;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px]"
      style={{ background: 'var(--accent-subtle)', border: '1px solid var(--border-default)' }}
      data-testid="active-context-bar"
    >
      <Layers className="w-3 h-3" style={{ color: 'var(--accent-primary)' }} />
      <span className="font-medium" style={{ color: 'var(--accent-primary)' }}>{activeProject.name}</span>
      {activeScenario && (
        <>
          <ChevronRight className="w-3 h-3" style={{ color: 'var(--accent-primary)', opacity: 0.5 }} />
          <span style={{ color: 'var(--accent-primary)' }}>{activeScenario.name}</span>
        </>
      )}
    </div>
  );
}
